import * as React from 'react';
import * as PropTypes from 'prop-types';
import { range, padStart } from 'lodash';
import { shouldUseAdyenCheckoutAPIForUpdates } from '@reverbdotcom/discovery-ui';
import TooltipPanel from '@reverbdotcom/commons/dist/components/tooltip_panel';
import CreditCardIcon from './credit_card_icon';
import InputMask from './input_mask';
import CardType from './card_type';

const asOption = (value, prefix, display = value) =>
  <option key={`${prefix}_${value}`} value={value}>{display}</option>;

const asTwoDigitMonthString = month => padStart(month.toString(), 2, '0');

const expirationMonthOptions = () => {
  const startMonth = 1;
  const endMonth = 12;
  return range(startMonth, endMonth + 1).map(month =>
    asOption(month.toString(), 'expMonth', asTwoDigitMonthString(month)));
};

const expirationYearOptions = () => {
  const startYear = new Date().getFullYear();
  const endYear = startYear + 10;
  return range(startYear, endYear).map(year => asOption(year.toString(), 'expYear'));
};

class AdyenCreditCardFormFields extends React.Component {
  componentDidMount() {
    InputMask.init();
    CardType.init();
  }

  cardholderNameInput() {
    return (
      <div className="scaling-mb-1">
        <dt>
          <label htmlFor="cardholderName">
            {I18n.t('js.creditCards.creditCardForm.nameOnCard')}
          </label>
        </dt>
        <dd>
          <input
            id="cardholderName"
            type="text"
            autoComplete="name"
            className="wide required"
            defaultValue={this.props.cardholderName}
            onChange={this.props.onChange}
          />
        </dd>
      </div>
    );
  }

  cardNumberInput() {
    return (
      <div>
        <dt>
          <label htmlFor="cardNumber">
            {I18n.t('js.creditCards.creditCardForm.cardNumber')}
          </label>
        </dt>
        <dd>
          <div className="position-relative">
            <input
              id="cardNumber"
              type="tel"
              className="wide required"
              data-tooltip
              data-credit-card-number
              onChange={this.props.onChange}
            />
            <div className="cc-type-icons">
              <div
                className="payment_method_icon payment_method"
                data-credit-card-type-target
              />
            </div>
          </div>
        </dd>
      </div>
    );
  }

  newCreditCardFormFields() {
    return (
      <dl className="label-pairs">
        {this.cardholderNameInput()}
        {this.cardNumberInput()}
        {this.expirationAndCVVFields()}
      </dl>
    );
  }

  updateCreditCardFormFields() {
    const updateCardTitle = I18n.t(
      'js.creditCards.creditCardForm.updateCardTitle',
      {
        cardType: this.props.cardType,
        lastFour: this.props.lastFour,
      },
    );

    return (
      <div>
        <h3 className="heading-3 mb-1 clearfix">
          <div className="pull-left">{updateCardTitle}</div>
          <div className="pull-right cc-header-icon">
            <CreditCardIcon cardType={this.props.cardType} />
          </div>
        </h3>
        {this.cardholderNameInput()}
        {this.expirationAndCVVFields()}
        <div className="separator-line" />
      </div>
    );
  }

  expirationAndCVVFields() {
    return (
      <div className="g-container">
        <div className="g-col-6 g-col-tablet-12">
          <label htmlFor="expirationMonth">
            {I18n.t('js.creditCards.creditCardForm.expirationDate')}
          </label>
          <div className="g-container">
            <div className="g-col-6">
              <div className="styled-dropdown">
                <select
                  id="expirationMonth"
                  onChange={this.props.onChange}
                  value={this.props.expirationMonth}
                >
                  {expirationMonthOptions()}
                </select>
              </div>
            </div>
            <div className="g-col-6">
              <div className="styled-dropdown">
                <select
                  id="expirationYear"
                  className="wide required"
                  onChange={this.props.onChange}
                  value={this.props.expirationYear}
                >
                  {expirationYearOptions()}
                </select>
              </div>
            </div>
          </div>
        </div>
        {this.renderCvv()}
      </div>
    );
  }

  renderCvv() {
    if (shouldUseAdyenCheckoutAPIForUpdates()) {
      return null;
    }

    return (
      <div className="g-col-2 g-col-tablet-12 g-offset-1 g-offset-tablet-0">
        <dt>
          <label htmlFor="cvv">
            <TooltipPanel text={I18n.t('js.creditCards.creditCardForm.cvvTooltip')}>
              {I18n.t('js.creditCards.creditCardForm.cvvLabel')}
            </TooltipPanel>
          </label>
        </dt>
        <dd>
          <input
            id="cvv"
            type="tel"
            className="wide cardholder_name required"
            maxLength="4"
            data-tooltip
            defaultValue={this.props.cvv}
            onChange={this.props.onChange}
          />
        </dd>
      </div>
    );
  }


  render() {
    if (this.props.isCreatingNewCreditCard) {
      return this.newCreditCardFormFields();
    }

    return this.updateCreditCardFormFields();
  }
}

AdyenCreditCardFormFields.propTypes = {
  onChange: PropTypes.func.isRequired,
  isCreatingNewCreditCard: PropTypes.bool,
  cardholderName: PropTypes.string,
  expirationMonth: PropTypes.string,
  expirationYear: PropTypes.string,
  cvv: PropTypes.string,
  cardType: PropTypes.string,
  lastFour: PropTypes.string,
};

AdyenCreditCardFormFields.defaultProps = {
  isCreatingNewCreditCard: true,
  cardholderName: '',
  expirationMonth: '',
  expirationYear: '',
  cvv: '',
  cardType: '',
  lastFour: '',
};

export default AdyenCreditCardFormFields;



// WEBPACK FOOTER //
// ./javascripts/components/credit_cards/adyen_credit_card_form_fields.jsx